import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { NanaeHeader, NanaeFooter } from "@/components/qv3/NanaeShared";
import { useAuth } from "@/contexts/AuthContext";
import { DevBanner } from "@/components/qorum/DevBanner";
import { CompteTabs } from "@/components/qorum/CompteTabs";
import "@/styles/compte-responsive.css";
import { socialMeta } from "@/lib/seo-meta";

export const Route = createFileRoute("/compte")({
  head: () => ({
    meta: [{ title: "Mon Compte — Hōjin" }, { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Mon Compte — Hōjin",
        description: "Mon Compte — Hōjin",
        url: "https://hojin.ai/compte",
        image: "secretaire",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/compte" }],
  }),
  beforeLoad: ({ location }) => {
    if (location.pathname === "/compte" || location.pathname === "/compte/") {
      throw redirect({ to: "/compte/profil" });
    }
  },
  component: CompteLayout,
});

const noiseSvg =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3CfeColorMatrix values='0 0 0 0 0.12 0 0 0 0 0.12 0 0 0 0 0.13 0 0 0 0.4 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.4'/%3E%3C/svg%3E\")";

function CompteLayout() {
  const { user, signOut } = useAuth();
  return (
    <ProtectedRoute>
      <DevBanner />
      <NanaeHeader
        title="Mon Compte"
        user={
          user
            ? {
                email: user.email,
                name:
                  (user.user_metadata as { first_name?: string } | undefined)?.first_name ??
                  user.email,
              }
            : null
        }
        rightSlot={
          <button
            type="button"
            onClick={() => void signOut()}
            className="qv3-btn-link"
            style={{ color: "inherit" }}
          >
            Déconnexion
          </button>
        }
      />
      <main
        style={{
          minHeight: "calc(100vh - 64px)",
          background: "var(--qv3-paper)",
          backgroundImage: noiseSvg,
          backgroundRepeat: "repeat",
        }}
      >
        <div className="nanae-compte-shell">
          {/* HERO */}
          <header className="nanae-compte-hero" style={{ borderBottom: "0.5px solid var(--qv3-grey-100)" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 20,
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 24,
                  height: 0.5,
                  background: "var(--qv3-gold)",
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  fontFamily: "var(--qv3-font-mono)",
                  fontSize: 10,
                  color: "var(--qv3-gold)",
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                }}
              >
                Mon Compte · Réglages personnels
              </span>
            </div>
            <h1
              style={{
                fontFamily: "var(--qv3-font-serif)",
                fontWeight: 400,
                fontSize: "clamp(36px, 7vw, 72px)",
                color: "var(--qv3-ink)",
                letterSpacing: "-1.5px",
                textTransform: "uppercase",
                lineHeight: 0.95,
                margin: 0,
              }}
            >
              Votre{" "}
              <em
                style={{
                  fontFamily: "var(--qv3-font-serif)",
                  fontWeight: 500,
                  fontStyle: "italic",
                  color: "var(--qv3-gold)",
                  textTransform: "none",
                  letterSpacing: 0,
                }}
              >
                compte.
              </em>
            </h1>
          </header>

          <CompteTabs />

          <div className="nanae-compte-body">
            <Outlet />
          </div>
        </div>
      </main>
      <NanaeFooter variant="minimal" />
    </ProtectedRoute>
  );
}
